import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button as MuiButton,
    TextField,
    CircularProgress,
} from '@mui/material';
import styled from 'styled-components';
import { FlexDiv, TextTiny } from '@awarego-ui/components';
import { useStores } from '../hooks/use-stores';
import services from '../services';

const companiesService = services.Companies;

const BadgeButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    cursor: pointer;
    border-radius: 6px;
    padding: 2px 6px;
    margin-left: 8px;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    vertical-align: middle;

    &:hover {
        background: #bae6fd;
    }

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const LocaleGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

function countFilled(map) {
    if (!map || typeof map !== 'object') return 0;
    return Object.values(map).filter((v) => String(v || '').trim().length > 0)
        .length;
}

function stripEmpty(map) {
    const out = {};
    for (const [k, v] of Object.entries(map || {})) {
        const val = String(v || '').trim();
        if (val) out[k] = val;
    }
    return out;
}

/**
 * Translation badge + modal for user-provided entity names
 * (SCORM package / training / assessment).
 */
function NameTranslationsControl({
    name,
    nameTranslations,
    entityId,
    companyId,
    itemStore,
    isScorm = false,
    maxLength = 50,
}) {
    const { store, commonStore } = useStores();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiError, setAiError] = useState(null);

    const aiDisabled = useMemo(() => {
        const v =
            store.currentCompanyUISettings?.[
                'ui.name_translations.ai_disabled'
            ];
        return v === '1' || v === 1 || v === true || v === 'true';
    }, [store.currentCompanyUISettings]);

    const languages = useMemo(() => {
        const codes = store.enabledLanguages || [];
        const index = commonStore.languagesIndex || {};
        return codes.map((code) => ({
            code,
            label: index[code]?.english_name || code,
        }));
    }, [store.enabledLanguages, commonStore.languagesIndex]);

    const filledCount = countFilled(nameTranslations);

    useEffect(() => {
        if (!open) return;
        const next = {};
        for (const { code } of languages) {
            next[code] = (nameTranslations && nameTranslations[code]) || '';
        }
        // Ensure canonical name shows for a primary locale when map is empty.
        if (name && !Object.values(next).some((v) => String(v).trim())) {
            const preferred =
                (languages.find((l) => l.code === 'en') && 'en') ||
                (languages[0] && languages[0].code);
            if (preferred) next[preferred] = name;
        }
        setDraft(next);
        setAiError(null);
    }, [open, languages, nameTranslations, name]);

    const onApply = async () => {
        if (!itemStore || !entityId) return;
        setSaving(true);
        try {
            const cleaned = stripEmpty(draft);
            // Canonical `name` stays the English (or existing) default; other
            // locales live only in nameTranslations.
            const primary = cleaned.en || name;
            const idForApi = `${isScorm ? 'p' : ''}${entityId}`;
            await itemStore.rename(companyId, idForApi, {
                name: primary,
                nameTranslations: cleaned,
            });
            setOpen(false);
        } catch (e) {
            console.error(e);
            setAiError('Failed to save translations.');
        } finally {
            setSaving(false);
        }
    };

    const onAutoTranslate = async () => {
        setAiBusy(true);
        setAiError(null);
        try {
            const sourceLang =
                (draft.en && draft.en.trim() && 'en') ||
                languages.find((l) => draft[l.code] && draft[l.code].trim())
                    ?.code;
            const sourceText = sourceLang
                ? String(draft[sourceLang] || '').trim()
                : '';
            if (!sourceText) {
                setAiError('Enter a source name before auto-translating.');
                return;
            }
            const emptyLanguages = languages
                .map((l) => l.code)
                .filter(
                    (code) =>
                        code !== sourceLang &&
                        !(draft[code] && String(draft[code]).trim())
                );
            if (emptyLanguages.length === 0) {
                setAiError('All locales already have a value.');
                return;
            }
            if (aiDisabled) {
                setAiError('AI name translation is disabled for this company.');
                return;
            }
            const result = await companiesService.translateName(
                companyId,
                emptyLanguages,
                { lang: sourceLang, value: sourceText }
            );
            const translations =
                (result && (result.translations || result)) || {};
            setDraft((prev) => {
                const next = { ...prev };
                for (const [k, v] of Object.entries(translations)) {
                    if (typeof v === 'string' && v.trim() && !next[k]?.trim()) {
                        next[k] = v.trim().slice(0, maxLength);
                    }
                }
                return next;
            });
        } catch (e) {
            console.error(e);
            const serverMsg =
                e?.response?.data?.error ||
                e?.response?.data?.message ||
                (typeof e?.response?.data === 'string'
                    ? e.response.data
                    : null) ||
                e?.message;
            setAiError(
                serverMsg
                    ? `Auto-translate failed: ${serverMsg}`
                    : 'Auto-translate failed. Please try again.'
            );
        } finally {
            setAiBusy(false);
        }
    };

    return (
        <>
            <BadgeButton
                type="button"
                onClick={() => setOpen(true)}
                title="Edit name translations"
                aria-label={`Name translations (${filledCount})`}
            >
                <span aria-hidden>文A</span>
                <span>{filledCount}</span>
            </BadgeButton>
            <Dialog
                open={open}
                onClose={() => !saving && setOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Translations — Name</DialogTitle>
                <DialogContent>
                    <TextTiny style={{ marginBottom: 16, opacity: 0.75 }}>
                        Enter the text for each language. Empty values are
                        removed on save.
                    </TextTiny>
                    {languages.length === 0 ? (
                        <TextTiny style={{ opacity: 0.75 }}>
                            No company languages enabled. Enable languages in
                            company settings first.
                        </TextTiny>
                    ) : (
                        <LocaleGrid>
                            {languages.map(({ code, label }) => (
                                <TextField
                                    key={code}
                                    label={`${label} (${code})`}
                                    value={draft[code] || ''}
                                    onChange={(e) =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            [code]: e.target.value.slice(
                                                0,
                                                maxLength
                                            ),
                                        }))
                                    }
                                    inputProps={{ maxLength }}
                                    fullWidth
                                    size="small"
                                />
                            ))}
                        </LocaleGrid>
                    )}
                    {aiError ? (
                        <TextTiny style={{ color: '#c00', marginTop: 12 }}>
                            {aiError}
                        </TextTiny>
                    ) : null}
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        px: 3,
                        pb: 2,
                    }}
                >
                    {aiDisabled ? (
                        <span />
                    ) : (
                        <MuiButton
                            onClick={onAutoTranslate}
                            disabled={aiBusy || saving}
                            startIcon={
                                aiBusy ? <CircularProgress size={14} /> : null
                            }
                        >
                            Auto-translate empty locales
                        </MuiButton>
                    )}
                    <FlexDiv gap={8}>
                        <MuiButton
                            onClick={() => setOpen(false)}
                            disabled={saving}
                        >
                            Cancel
                        </MuiButton>
                        <MuiButton
                            variant="contained"
                            onClick={onApply}
                            disabled={saving || aiBusy}
                        >
                            {saving ? 'Saving…' : 'Apply'}
                        </MuiButton>
                    </FlexDiv>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default observer(NameTranslationsControl);
